import { NearbyRadius, type RestaurantDetails } from "@/API";
import { useState, type FC } from "react";
import { Skeleton } from "../ui/skeleton";
import Text from "../Text";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { getRestaurantDetails } from "@/store/restaurantDetailsState";
import Badge from "../Badge";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { getColorKrunshScore } from "@/lib/utils";
import Explorer from "@/pages/Explorer";
import { currentSubscriptionState } from "@/store/currentSubscriptionState";
import { Lock } from "lucide-react";
import { setActionTriggerNeedCore } from "@/store/restrictedFeatureState";
import SelectMultiple from "../SelectMultiple";

type Props = {
  isLoading: boolean;
  restaurant?: RestaurantDetails;
  isFetching: boolean;
};

const limiTagsInCommonOptions = [1, 2, 3, 4, 5];

const RestaurantRanking: FC<Props> = ({
  isLoading,
  restaurant,
  isFetching,
}) => {
  const { t } = useTranslation();
  const { data: planUser } = currentSubscriptionState();

  const [distance, setDistance] = useState<NearbyRadius>(
    planUser ? NearbyRadius.TWO_KILOMETERS : NearbyRadius.TWO_HUNDRED_METERS,
  );
  const [numberOfTagsInCommon, setNumberOfTagsInCommon] = useState<number>(1);
  const [commonTags, setCommonTags] = useState<string[]>([]);
  const { pathname } = useLocation();
  const isExplorer = pathname.includes("/explorer");

  if (isLoading) return <Skeleton />;

  if (!restaurant) return null;

  if (isFetching)
    return <Skeleton className="h-[60px] w-full rounded-[12px]" />;

  const { nearbyRestaurants, name, id } = restaurant;

  const nearbyRestaurantsFilter = nearbyRestaurants.restaurants.filter(
    (it) => it !== null,
  );

  const NumberOfNearbyRestaurants = nearbyRestaurantsFilter.length;
  const positionOfOurRestaurant = nearbyRestaurantsFilter.findIndex(
    (it) => name === it?.name,
  );

  return (
    <div className="w-[100%]">
      <div className="mb-[24px] flex gap-[16px] flex-wrap">
        <Select
          value={distance}
          onValueChange={(value: NearbyRadius) => {
            if (!planUser && value !== NearbyRadius.TWO_HUNDRED_METERS) {
              setActionTriggerNeedCore(true);
            } else {
              getRestaurantDetails({
                id: restaurant.id,
                nearbyRadius: value,
                nearbyTagsInCommon: numberOfTagsInCommon,
                tagsExactMatch: commonTags,
              });
              setDistance(value);
            }
          }}
        >
          <SelectTrigger style={{ border: "none" }} className="bg-[#F1F2F4]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={NearbyRadius.TWO_HUNDRED_METERS}>
                200 M
              </SelectItem>
              <SelectItem value={NearbyRadius.FIVE_HUNDRED_METERS}>
                {!planUser && <Lock color="#44546F" height={18} width={18} />}
                500 M
              </SelectItem>
              <SelectItem value={NearbyRadius.ONE_KILOMETER}>
                {!planUser && <Lock color="#44546F" height={18} width={18} />}1
                KM
              </SelectItem>
              <SelectItem value={NearbyRadius.TWO_KILOMETERS}>
                {!planUser && <Lock color="#44546F" height={18} width={18} />}2
                KM
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>

        <Select
          value={numberOfTagsInCommon.toString()}
          onValueChange={(value: string) => {
            if (!planUser && value !== "1") {
              setActionTriggerNeedCore(true);
            } else {
              getRestaurantDetails({
                id: restaurant.id,
                nearbyRadius: distance,
                nearbyTagsInCommon: Number(value),
                tagsExactMatch: commonTags,
              });
              setNumberOfTagsInCommon(Number(value));
            }
          }}
        >
          <SelectTrigger style={{ border: "none" }} className="bg-[#F1F2F4]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {limiTagsInCommonOptions.map((option) => (
                <SelectItem value={option.toString()}>
                  {!planUser && option !== 1 && (
                    <Lock color="#44546F" height={18} width={18} />
                  )}
                  {option === 1
                    ? t("category_in_common", { number: option })
                    : t("categories_in_common", { number: option })}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <SelectMultiple
          placeholder="Selectionner des catégories"
          className="max-w-[320px] w-[320px] !bg-[#F1F2F4]"
          isRestricted={!planUser}
          value={commonTags}
          onChange={(value) => {
            getRestaurantDetails({
              id: restaurant.id,
              nearbyRadius: distance,
              nearbyTagsInCommon: numberOfTagsInCommon,
              tagsExactMatch: value,
            });
            setCommonTags(value);
          }}
          options={
            restaurant.categories
              ?.map((category) =>
                category
                  ? {
                      label: category,
                      value: category,
                    }
                  : null,
              )
              .filter((it) => it !== null) ?? []
          }
        />
      </div>

      {!isExplorer && (
        <div className="flex gap-[14px] overflow-x-auto mb-[24px] w-[100%]">
          <div className="bg-[white] min-w-[194px] w-[100%] h-[116px] flex flex-col items-center justify-center rounded-[8px] gap-[8px]">
            <Text
              fontSize={20}
              fontWeight={600}
              color={
                nearbyRestaurants.averageRating > Number(restaurant.rating)
                  ? "#E2483D"
                  : "#22A06B"
              }
            >
              {nearbyRestaurants.averageRating}
            </Text>
            <Text fontSize={16} fontWeight={400}>
              {t("average_rating")}
            </Text>
            <Text fontSize={14} fontWeight={400}>
              {`vs ${restaurant.rating} ${t("for_you")}`}
            </Text>
          </div>

          <div className="bg-[white] min-w-[194px] w-[100%] h-[116px] flex flex-col items-center justify-center rounded-[8px] gap-[8px]">
            <Text
              fontSize={20}
              fontWeight={600}
              color={
                nearbyRestaurants.averageScore > restaurant.krunshScore
                  ? "#E2483D"
                  : "#22A06B"
              }
            >
              {nearbyRestaurants.averageScore}
            </Text>
            <Text fontSize={16} fontWeight={400}>
              {t("average_krunsh_score")}
            </Text>
            <Text fontSize={14} fontWeight={400}>
              {`vs ${restaurant.krunshScore} ${t("for_you")}`}
            </Text>
          </div>

          <div className="bg-[white] min-w-[194px] w-[100%] h-[116px] flex flex-col items-center justify-center rounded-[8px] gap-[8px]">
            <Text
              fontSize={20}
              fontWeight={600}
              color={
                nearbyRestaurants.averageDeliveryTime <
                Number(restaurant.deliveryTime)
                  ? "#E2483D"
                  : "#22A06B"
              }
            >
              {nearbyRestaurants.averageDeliveryTime}
            </Text>
            <Text fontSize={16} fontWeight={400}>
              {t("average_delivery_time")}
            </Text>
            <Text fontSize={14} fontWeight={400}>
              {`vs ${restaurant.deliveryTime} ${t("for_you")}`}
            </Text>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-[13px]">
        <div className="bg-[white] p-[16px] rounded-[16px] flex items-center h-[80px] w-[fit-content]">
          <Text fontWeight={400} fontSize={14} color="black">
            #
          </Text>
          <Text fontWeight={600} fontSize={32} color="black">
            {positionOfOurRestaurant + 1}
          </Text>
          <Text
            fontWeight={600}
            fontSize={14}
            color="black"
          >{`/${NumberOfNearbyRestaurants}`}</Text>
        </div>
        <div className="flex flex-col gap-[16px] overflow-x-hidden w-[100%]">
          {nearbyRestaurantsFilter.map((it, index) => (
            <Link
              to={`/explorer/restaurant/${it.id}`}
              key={`${it.name}-${index}`}
              className="bg-[white] p-[24px] rounded-[24px] flex justify-between cursor-pointer items-center overflow-x-hidden"
              style={
                it.id === id && !Explorer
                  ? {
                      border: "1px #44546F solid",
                    }
                  : undefined
              }
            >
              <div className="flex items-center overflow-x-hidden">
                <div className="bg-[#F7F8F9] rounded-[12px] grid place-items-center h-[40px] min-w-[40px] w-[40px] mr-[12px]">
                  {index + 1}
                </div>
                <img
                  src={it.imageUrl ?? undefined}
                  className="object-cover h-[32px] w-[32px] rounded-[50%]"
                />
                <img
                  className="h-[32px] w-[32px] ml-[-16px] rounded-[50%] object-cover"
                  src={`/partner/${restaurant.platform.toLowerCase()}.png`}
                  alt={restaurant.platform}
                />
                <div className="flex flex-col gap-[8px] overflow-x-hidden ml-[30px] pr-[16px]">
                  {it.id === id && !isExplorer && (
                    <div className="bg-[#F7F8F9] py-[4px] px-[8px] rounded-[40px] w-[fit-content]">
                      <Text fontSize={14} fontWeight={600}>
                        {t("your_restaurant")}
                      </Text>
                    </div>
                  )}
                  <Text fontWeight={600} fontSize={20} className="truncate">
                    {it.name}
                  </Text>
                  <Text fontSize={14} fontWeight={400} className="truncate">
                    {it.address}
                  </Text>
                  <div className="flex gap-[8px] overflow-x-auto">
                    <Badge className="bg-[#44546F]">
                      <Text fontSize={14} fontWeight={600} color="#F7F8F9">
                        {`${(it.distance * 1000).toFixed(0)}m`}
                      </Text>
                    </Badge>
                    {it.categories
                      .filter((catgetory) => catgetory !== null)
                      .map((category) => (
                        <Badge key={category}>
                          <Text fontSize={14} fontWeight={400}>
                            {category}
                          </Text>
                        </Badge>
                      ))}
                  </div>
                </div>
              </div>

              <div
                className="bg-[white] h-[40px] min-h-[40px] w-[40px] min-w-[40px] rounded-[50%] flex items-center justify-center"
                style={{
                  border: `solid 2px ${getColorKrunshScore(it.krunshScore)}`,
                }}
              >
                <Text
                  fontWeight={600}
                  fontSize={16}
                  color={getColorKrunshScore(it.krunshScore)}
                >
                  {it.krunshScore}
                </Text>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RestaurantRanking;
